import { UrlService } from '@/common/services/url/url.service';
import { fromISOtoDate } from '@/common/utils/date/fromISOtoDate.util';
import { fromJSDateToISODate } from '@/common/utils/date/fromJSDateToISODate';
import { FindSchedulesUseCase } from '@/core/domain/locations/usecases/findSchedules.usecase';
import { GetPrecheckinUseCase } from '@/core/domain/precheckin/usecases/get-precheckin.usecase';
import { SendChangeHourUseCase } from '@/core/domain/precheckin/usecases/send-change-hour.usecase';
import { UpdatePrecheckinUseCase } from '@/core/domain/precheckin/usecases/updatePrecheckin.usecase';
import { Component, Inject, ViewChild, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { SwalComponent } from '@sweetalert2/ngx-sweetalert2';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { of, switchMap } from 'rxjs';
import { SweetAlertResult } from 'sweetalert2';

@Component({
    selector: 'fe-card-active-reservation',
    templateUrl: './card-active-reservation.component.html',
    styleUrls: ['./card-active-reservation.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class CardActiveReservationComponent {
    public reservationDetails: any = {};

    @ViewChild('changeHourTemplate') changeHourTemplate!: SwalComponent;

    public startHours: any[] = [];

    public selectedHour!: string;

    constructor(
        @Inject('DATE_FORMAT_PROGRESS_BAR')
        public dateFormatProgressBar: string,
        private getPrecheckinUseCase: GetPrecheckinUseCase,
        private readonly findSchedulesUseCase: FindSchedulesUseCase,
        private readonly sendChangeHourUseCase: SendChangeHourUseCase,
        private updatePrecheckinUseCase: UpdatePrecheckinUseCase,
        private url: UrlService,
        private router: Router,
    ) {
        this.initData();
    }

    private initData(): void {
        this.getPrecheckinUseCase
            .execute()
            .pipe(
                switchMap((response) => {
                    this.reservationDetails = response;
                    if (this.reservationDetails?.status === 'CANCELED') {
                        this.router.navigateByUrl(this.url.format(''));
                    }
                    this.getSchedules();
                    return of(response);
                }),
            )
            .subscribe();
    }

    private getSchedules(): void {
        this.findSchedulesUseCase
            .execute({
                id: this.reservationDetails.pickupBranch.id,
                location: 'NAT',
                pickup: fromJSDateToISODate(
                    fromISOtoDate(this.reservationDetails.pickupDate),
                ),
                return: fromJSDateToISODate(
                    fromISOtoDate(this.reservationDetails.returnDate),
                ),
            })
            .pipe(
                switchMap((schedulesResponse) => {
                    this.onGetSchedules(schedulesResponse);
                    return of(schedulesResponse);
                }),
            )
            .subscribe();
    }

    private onGetSchedules(hours: any): void {
        this.startHours = _.get(hours, 'data.schedules.pickup', []);
        const startHour = DateTime.fromISO(
            this.reservationDetails.pickupDate,
        ).toFormat('HH:mm');
        const remove: string[] = [];
        _.some(this.startHours, (item) => {
            remove.push(item);
            if (item === startHour) {
                return true;
            }
            return false;
        });
        this.startHours = _.difference(this.startHours, remove);
    }

    public onClickChangeHour(): void {
        this.changeHourTemplate.fire().then((response: SweetAlertResult) => {
            const pickupDate = DateTime.fromISO(
                this.reservationDetails.pickupDate,
            ).toISODate();
            if (response.isConfirmed) {
                const sendNewDate =
                    pickupDate + 'T' + this.selectedHour + ':00';
                const precheckin = {
                    pickupDate: sendNewDate,
                };
                this.updatePrecheckinUseCase.execute(precheckin).subscribe();
            }
        });
    }

    public sendNewDate(date: string) {
        this.sendChangeHourUseCase.execute(date).subscribe();
    }

    getStatusText(): string {
        const status = this.reservationDetails?.status;

        switch (status) {
            case 'PENDING':
                return 'fe_precheckin_pending';
            case 'CONFIRMED':
                return 'fe_details_step5_delivery_cancel_parent_top_title_confirmed';
            case 'CANCELED':
                return 'fe_details_step5_delivery_cancel_parent_top_title_canceled';
            case 'PAID':
                return 'fe_details_step5_delivery_cancel_parent_top_title_paid';
            case 'REJECTED':
                return 'fe_details_step5_delivery_cancel_parent_top_chip_rejected';
            case 'PRECHECKIN':
                return 'fe_details_step5_delivery_cancel_parent_top_title_precheckin';
            default:
                return 'fe_details_step5_delivery_cancel_parent_top_title_default';
        }
    }
}
